import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface AppShellProps {
  sidebar: ReactNode;
  main: ReactNode;
  /** Right context panel — hidden on mobile per Responsive Spec §03. */
  aside?: ReactNode;
  /** Mobile bottom tab bar shown on small screens. */
  mobileNav?: ReactNode;
  className?: string;
}

/**
 * Canonical InkFinity app shell: 240 sidebar / 1fr / 280 right panel.
 * Collapses to single-column on mobile (via Tailwind responsive utilities).
 *
 * Use alongside TopBar / Composer for fully composed layouts.
 */
export function AppShell({ sidebar, main, aside, mobileNav, className }: AppShellProps) {
  return (
    <div
      className={cn(
        // Desktop (≥1025): 3-column. Tablet: 2-column with collapsible sidebar. Mobile: single column.
        'grid grid-cols-1 md:grid-cols-[240px_1fr] lg:grid-cols-[240px_1fr_280px]',
        'overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface',
        'h-[720px]',
        className
      )}
    >
      <aside
        className="hidden flex-col border-r border-border-subtle bg-bg-surface md:flex"
        role="complementary"
        aria-label="Navigation"
      >
        {sidebar}
      </aside>
      <main className="flex min-w-0 flex-col overflow-hidden bg-bg-page">{main}</main>
      {aside && (
        <aside
          className="hidden flex-col border-l border-border-subtle bg-bg-surface lg:flex"
          role="complementary"
          aria-label="Context"
        >
          {aside}
        </aside>
      )}
      {mobileNav && (
        <nav className="absolute bottom-0 left-0 right-0 flex items-center justify-around border-t border-border-subtle bg-bg-surface px-2 py-2 md:hidden">
          {mobileNav}
        </nav>
      )}
    </div>
  );
}

/**
 * App brand mark. Used inside the sidebar header.
 */
export function SidebarBrand({ name = 'InkFinity' }: { name?: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-border-subtle px-5 py-4 font-semibold">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-400 font-serif text-sm text-white">
        I
      </div>
      <span>{name}</span>
    </div>
  );
}

/** Sidebar nav item — active state set externally. */
export function SidebarItem({
  active,
  icon,
  children,
  onClick,
}: {
  active?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'mx-2 flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm',
        'w-[calc(100%-16px)] text-left transition-colors duration-fast',
        active
          ? 'bg-brand-50 font-medium text-brand-700'
          : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
      )}
    >
      {icon && <span className="inline-flex h-4 w-4 items-center justify-center">{icon}</span>}
      {children}
    </button>
  );
}
